import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, Col, Row } from "reactstrap";
import ExportContent from "./exportContent";
import QuoteExportPreview from "./quoteExportPreview";
import { useExportRequestData } from "../../../hooks/useExportRequestData";
import { fetchExportTemplate } from "../../exportTemplates/sheet/data/exportTemplateService";
import { fetchDocumentTemplate } from "../../exportTemplates/document/data/documentTemplateService";

const QuoteExports = ({
    quoteRevision,
    quoteItems,
    onUpdateQuoteAdditionalFields = () => {}
}) => {
    const [selectedExportTemplate, setSelectedExportTemplate] = useState(null);

    const {
        salesForceData,
        customContent,
        exportData,
        isLoadingOpportunity,
        isLoadingTemplates,
    } = useExportRequestData(
        quoteRevision,
        quoteItems,
        quoteRevision?.quoteCustomisation,
        selectedExportTemplate
    );

    // Both fetches can return either a plain array or a normalized `{ [itemsKey]: [...] }` wrapper
    // (e.g. when the API responds with a paged result) — unwrap before mapping so one wrapped
    // response doesn't reject the whole query and blank out both sheets and documents.
    const toTemplateArray = (result, itemsKey) => {
        if (Array.isArray(result)) return result;
        if (Array.isArray(result?.[itemsKey])) return result[itemsKey];
        return [];
    };

    const getTemplates = async () => {
        const excelTemplatesResult = await fetchExportTemplate()
        const documentTemplatesResult = await fetchDocumentTemplate();

        const excelTemplates = toTemplateArray(excelTemplatesResult, "exportTemplateItems");
        const documentTemplates = toTemplateArray(documentTemplatesResult, "documentTemplateItems");

        const sheetTemplates = excelTemplates.filter(x => !x.disabled).map(x => ({ ...x, type: "sheet" }));
        const docTemplates = documentTemplates.filter(x => !x.disabled).map(x => ({ ...x, type: "document" }));
        return [...sheetTemplates, ...docTemplates];
    }

    const {
        data: exportTemplateData = [],
        isLoading: isLoadingExportTemplates,
    } = useQuery({
        queryKey: ["exportTemplatesTab"],
        queryFn: getTemplates,
        staleTime: 60000,
    });

    const previewTemplate = useMemo(() => selectedExportTemplate ?? null, [selectedExportTemplate]);
    const canPreviewTemplate = useMemo(() => {
        if (!previewTemplate) return false;
        if (previewTemplate.type === "sheet") return true;
        return !!previewTemplate.customFields;
    }, [previewTemplate]);

    const isAnyLoading = isLoadingTemplates || isLoadingOpportunity || isLoadingExportTemplates;

    return (
        <Row className="align-items-stretch" style={{ height: "calc(100vh - 180px)", minHeight: "400px" }}>
            <Col className="d-flex flex-column h-100" style={{ minHeight: 0, overflow: "hidden" }}>
                <Card style={{ width: "100%", height: "100%", minHeight: 0 }}>
                    <CardBody className="d-flex flex-column h-100" style={{ minHeight: 0, overflow: "hidden" }}>
                        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
                            <ExportContent
                                exportTemplateData={exportTemplateData}
                                selectedTemplate={selectedExportTemplate}
                                setSelectedTemplate={setSelectedExportTemplate}
                                quoteRevision={quoteRevision}
                                customContent={customContent}
                                onUpdateQuoteAdditionalFields={onUpdateQuoteAdditionalFields}
                            />
                        </div>
                    </CardBody>
                </Card>
            </Col>
            <Col className="d-flex flex-column h-100" style={{ minHeight: 0, overflow: "hidden" }}>
            
                {isAnyLoading ? (
                    <div style={{ width: "100%", height: "100%", minHeight: 0 }}>
                        <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-3 mb-0">Loading...</p>
                        </div>
                    </div>
                ) : (
                    canPreviewTemplate ? (
                        <QuoteExportPreview
                            quoteItems={quoteItems}
                            groupedQuoteItems={exportData}
                            salesForceData={salesForceData}
                            customContent={customContent}
                            template={previewTemplate}
                            allowDownload={true}
                            revisionNumber={quoteRevision?.revisionNumber}
                            mode="preview"
                        />
                    ) : (
                        <div style={{ width: "100%", height: "100%", minHeight: 0 }}>
                            <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
                                <i className="bi bi-file-earmark-text fs-1"></i>
                                <p className="mt-2 mb-0">
                                    {selectedExportTemplate
                                        ? "Preview is not available for this export template yet."
                                        : "Please select an export template."}
                                </p>
                            </div>
                        </div>
                    )
                )}
            </Col>
        </Row>
    );
}

export default QuoteExports;